import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { socialApi, SocialStatus, SocialPlattform, SocialKontoArt, SocialConnectionInfo } from '../services/api';
import { useToast } from '../sectepe-ui/index';
import { useConfirm } from '../components/ui';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { PLATTFORM_NAME, PLATTFORMEN, kostenText } from './plattformen';
import { useVeroeffentlichungsprofile } from './useVeroeffentlichungsprofile';

const TEAM_STATUS: Record<string, string> = {
  aktiv: 'Aktiv',
  laeuftAb: 'Läuft bald ab',
  abgelaufen: 'Abgelaufen – die Person verbindet neu',
  getrennt: 'Getrennt',
};
const TEAM_PLATTFORM: Record<string, string> = { linkedin: 'LinkedIn', x: 'X', instagram: 'Instagram', facebook: 'Facebook' };

/**
 * "Konten & Kanäle": Instagram, X und Facebook mit Firmenkonto (ganzer
 * Mandant) und persönlichem Konto (nur für mich), Testmodus, X-Kosten des
 * Monats und Instagram-Beiträge der letzten 24 Stunden. Facebook kennt nur
 * Seiten (Firmenkonten). Verarbeitet die Rückkehr-Parameter der Anmeldung
 * (?social_connected, ?social_error, ?facebook_auswahl).
 */
export const SocialKonten: React.FC = () => {
  const [status, setStatus] = useState<SocialStatus | null>(null);
  const [laden, setLaden] = useState(true);
  const [busy, setBusy] = useState(false);
  const [auswahl, setAuswahl] = useState<{ id: string; seiten: Array<{ id: string; name: string }>; gewaehlt: string[] } | null>(null);
  const { push } = useToast();
  const { confirm } = useConfirm();
  const { darf } = useBerechtigung();
  const darfFirma = darf('linkedin.freigeben');
  const darfPerson = darf('linkedin.bearbeiten');
  const location = useLocation();
  const navigate = useNavigate();
  // Persoenliche Profile des Teams (nur lesend; verbunden wird unter "Mein Profil verbinden").
  const team = useVeroeffentlichungsprofile();
  const teamProfile = team.profile.filter((p) => p.art === 'person' && p.status !== 'getrennt');

  const load = useCallback(async () => {
    try {
      setLaden(true);
      setStatus((await socialApi.getStatus()) ?? null);
    } catch {
      setStatus(null);
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Rueckkehr aus der Anmeldung: melden, Seitenauswahl oeffnen, URL aufraeumen.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verbunden = params.get('social_connected');
    const fehler = params.get('social_error');
    const auswahlId = params.get('facebook_auswahl');
    if (!verbunden && !fehler && !auswahlId) return;
    if (verbunden) {
      push({ message: `${PLATTFORM_NAME[verbunden as SocialPlattform] || 'Konto'} verbunden.`, severity: 'success' });
      load();
    }
    if (fehler) push({ message: fehler, severity: 'error', duration: 0 });
    if (auswahlId) {
      (async () => {
        try {
          const r = await socialApi.facebookSeiten(auswahlId);
          const seiten = r?.seiten ?? [];
          setAuswahl({ id: auswahlId, seiten, gewaehlt: seiten.slice(0, 1).map((s) => s.id) });
        } catch (err: any) {
          push({ message: err?.response?.data?.error || 'Die Facebook-Seiten konnten nicht geladen werden.', severity: 'error' });
        }
      })();
    }
    for (const k of ['social_connected', 'social_error', 'facebook_auswahl']) params.delete(k);
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}`, { replace: true });
  }, [location.search, location.pathname, navigate, push, load]);

  const verbinden = async (platform: SocialPlattform, art: SocialKontoArt) => {
    try {
      setBusy(true);
      const { authUrl } = await socialApi.initiateOAuth(platform, art);
      window.location.href = authUrl;
    } catch (err: any) {
      push({ message: err?.response?.data?.error || `Fehler beim Verbinden mit ${PLATTFORM_NAME[platform]}`, severity: 'error' });
      setBusy(false);
    }
  };

  const trennen = async (conn: SocialConnectionInfo) => {
    const ok = await confirm({
      title: `${PLATTFORM_NAME[conn.platform]}-Verbindung trennen?`,
      message: 'Geplante Einträge mit diesem Konto lassen sich danach erst nach erneutem Verbinden veröffentlichen.',
      confirmLabel: 'Trennen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      setBusy(true);
      await socialApi.disconnect(conn.id);
      push({ message: 'Verbindung getrennt.', severity: 'success' });
      await load();
    } catch (err: any) {
      push({ message: err?.response?.data?.error || 'Fehler beim Trennen der Verbindung', severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const seitenUebernehmen = async () => {
    if (!auswahl) return;
    try {
      setBusy(true);
      await socialApi.facebookSeitenUebernehmen(auswahl.id, auswahl.gewaehlt);
      push({ message: 'Facebook-Seite verbunden.', severity: 'success' });
      setAuswahl(null);
      await load();
    } catch (err: any) {
      push({ message: err?.response?.data?.error || 'Die Facebook-Seiten konnten nicht verbunden werden.', severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const konten = (platform: SocialPlattform, art: SocialKontoArt) =>
    (status?.connections ?? []).filter((c) => c.platform === platform && c.art === art);

  const kontoListe = (liste: SocialConnectionInfo[], darfTrennen: boolean) => (
    <ul className="linkedin-card__list">
      {liste.map((conn) => (
        <li key={conn.id} className="linkedin-card__conn">
          <div className="linkedin-card__conn-text">
            <strong>{conn.accountName || conn.accountId}</strong>
            <span className="text-xs text-muted">{conn.isActive ? 'Aktiv' : 'Abgelaufen – bitte neu verbinden'}</span>
          </div>
          {darfTrennen && (
            <div className="linkedin-card__actions">
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => trennen(conn)} disabled={busy}>
                Trennen
              </button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <section className="card" aria-label="Konten und Kanäle" style={{ marginBottom: 'var(--space-5)' }}>
      <h2 className="card-title">Konten &amp; Kanäle</h2>
      <p className="text-sm text-secondary">
        Firmenkonten sieht und nutzt der ganze Mandant. Persönliche Profile verbindet jede Person selbst unter „Mein Profil verbinden“;
        wer Beiträge freigeben darf, kann darüber freigegebene Beiträge veröffentlichen. Veröffentlicht wird aus „Terminieren“.
      </p>
      {laden ? (
        <p className="text-sm text-secondary">Status wird geladen…</p>
      ) : !status ? (
        <p className="text-sm text-secondary">Der Status von Instagram, X und Facebook ist gerade nicht abrufbar.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
          {PLATTFORMEN.map((platform) => {
            const p = status.plattformen?.[platform];
            const name = PLATTFORM_NAME[platform];
            return (
              <div key={platform} className="info-panel" style={{ margin: 0 }} data-testid={`social-karte-${platform}`}>
                <div className="info-panel-title">
                  {name}{' '}
                  {p?.dryRun && <span className="badge badge-warning">Testmodus</span>}
                  {!p?.configured && <span className="badge badge-neutral">Nicht konfiguriert</span>}
                </div>
                {!p?.configured ? (
                  <p className="text-sm text-secondary">
                    Die App-Zugangsdaten fehlen. Ein:e Inhaber:in trägt sie unter Verwaltung → Optionen ein.
                  </p>
                ) : (
                  <>
                    <div className="text-sm"><strong>{platform === 'facebook' ? 'Facebook-Seiten' : 'Firmenkonto'}</strong></div>
                    {konten(platform, 'mandant').length > 0 ? (
                      kontoListe(konten(platform, 'mandant'), darfFirma)
                    ) : (
                      <p className="text-xs text-muted">Noch nicht verbunden.</p>
                    )}
                    {darfFirma ? (
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => verbinden(platform, 'mandant')} disabled={busy}>
                        {platform === 'facebook' ? 'Seite verbinden' : 'Firmenkonto verbinden'}
                      </button>
                    ) : (
                      <p className="text-xs text-muted">Firmenkonten verbindet, wer Beiträge freigeben darf.</p>
                    )}
                    {platform === 'facebook' ? (
                      <p className="text-xs text-muted" style={{ marginTop: '8px' }}>
                        Facebook erlaubt das Posten nur auf Seiten, nicht auf persönlichen Profilen. Nötig ist die Admin- oder Inhalte-Rolle auf der Seite.
                      </p>
                    ) : (
                      <>
                        <div className="text-sm" style={{ marginTop: '8px' }}><strong>Persönliches Konto</strong></div>
                        {konten(platform, 'person').length > 0 ? (
                          kontoListe(konten(platform, 'person'), true)
                        ) : (
                          darfPerson && (
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => verbinden(platform, 'person')} disabled={busy}>
                              Eigenes Konto verbinden
                            </button>
                          )
                        )}
                      </>
                    )}
                  </>
                )}
                {platform === 'x' && (
                  <p className="text-xs text-secondary" style={{ marginTop: '8px' }}>
                    Kosten {status.verbrauch?.x?.monat}: ca. {kostenText(status.verbrauch?.x?.kostenCents)} für {status.verbrauch?.x?.beitraege ?? 0} Beiträge
                    {' '}(Schätzung, Pay-per-use)
                  </p>
                )}
                {platform === 'instagram' && (
                  <p className="text-xs text-secondary" style={{ marginTop: '8px' }}>
                    Letzte 24 Stunden: {status.verbrauch?.instagram?.letzte24h ?? 0} von {status.verbrauch?.instagram?.limit ?? 100} Beiträgen
                  </p>
                )}
                {p?.dryRun && (
                  <p className="text-xs text-muted">Testmodus: es wird nichts auf {name} gepostet.</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 'var(--space-5)' }} data-testid="team-profile">
        <h3 className="form-section-title">Persönliche Profile im Team</h3>
        <p className="text-xs text-muted">
          Nur zur Übersicht: Teammitglieder verbinden und trennen ihr eigenes LinkedIn-, X- oder Instagram-Konto selbst
          unter <Link to="/mein-profil">„Mein Profil verbinden“</Link> – dafür brauchen sie keinen Zugang zu diesen Einstellungen.
        </p>
        {team.geladen && teamProfile.length === 0 ? (
          <p className="text-sm text-secondary">Noch hat niemand im Team ein persönliches Profil verbunden.</p>
        ) : (
          <ul className="linkedin-card__list">
            {teamProfile.map((p) => (
              <li key={p.id} className="linkedin-card__conn">
                <div className="linkedin-card__conn-text">
                  <strong>{p.person?.name || p.kontoName}</strong>
                  <span className="text-xs text-muted">
                    {TEAM_PLATTFORM[p.plattform] || p.plattform} · {p.kontoName}
                  </span>
                </div>
                <span className={`badge ${p.status === 'aktiv' ? 'badge-success' : 'badge-warning'}`}>{TEAM_STATUS[p.status] || p.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {auswahl && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Facebook-Seiten wählen</h2>
              <button onClick={() => setAuswahl(null)} className="modal-close" aria-label="Schließen">x</button>
            </div>
            {auswahl.seiten.length === 0 ? (
              <p className="text-sm text-muted">Die Auswahl ist abgelaufen. Bitte Facebook erneut verbinden.</p>
            ) : (
              <fieldset className="form-group" style={{ border: 'none', padding: 0 }}>
                <legend className="form-label">Auf welchen Seiten soll Intermal posten dürfen?</legend>
                {auswahl.seiten.map((s) => (
                  <label key={s.id} style={{ display: 'block', marginBottom: '6px' }}>
                    <input
                      type="checkbox"
                      checked={auswahl.gewaehlt.includes(s.id)}
                      onChange={(e) => setAuswahl({
                        ...auswahl,
                        gewaehlt: e.target.checked ? [...auswahl.gewaehlt, s.id] : auswahl.gewaehlt.filter((g) => g !== s.id),
                      })}
                    />{' '}
                    {s.name}
                  </label>
                ))}
              </fieldset>
            )}
            <div className="btn-group mt-6">
              <button className="btn btn-success" onClick={seitenUebernehmen} disabled={busy || auswahl.gewaehlt.length === 0}>
                Verbinden
              </button>
              <button className="btn btn-secondary" onClick={() => setAuswahl(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SocialKonten;
